export default {
  name: {
    id: 'rm_metal_copper_name',
    defaultMessage: 'Copper',
  },
  smartphoneLabel: {
    id: 'rm_metal_copper_smartphone_label',
    defaultMessage: 'Major',
  },
  whatItDoesText: {
    id: 'rm_metal_copper_what_it_does',
    defaultMessage:
      'Copper acts as an electrical highway in our electronics, carrying signals between components. It is used in the solder that attaches components to the circuit board and connects the camera sensor to the rest of the phone.',
  },
  extractionText1: {
    id: 'rm_metal_copper_extraction_p1',
    defaultMessage:
      'Copper is found in sulfur-containing rock. Much of the world’s copper comes from large open-pit mines; underground mining also remains significant. Both methods involve processing large volumes of ore and waste rock, often with fossil-fuel-powered machinery. As rich deposits are depleted, ever-larger volumes must be processed, increasing energy use. Copper is separated from surrounding rock using heat or chemicals. This process uses water and produces mine tailings. Many copper tailings contain toxic substances and are often stored in ponds or other tailings storage facilities, creating risks of leaks and contamination.',
  },
  quickFactText: {
    id: 'rm_metal_copper_quick_fact',
    defaultMessage:
      'As copper ore quality worsens, extracting 2.2 lbs of copper requires processing even more rock, water, and land.',
  },
  envSummaryText: {
    id: 'rm_metal_copper_env_summary',
    defaultMessage:
      'Copper mining is highly water- and energy-intensive, generating significant environmental impacts. Half of global copper production is concentrated in the world’s driest regions, where mines compete with communities and ecosystems for increasingly scarce water. As one of the highest-volume mining activities on Earth, declining ore grades compound the problem: less metal, more disturbed rock, a higher risk of acid mine drainage, more waste rock, and higher water consumption.',
  },
  envWaterText: {
    id: 'rm_metal_copper_env_water_text',
    defaultMessage:
      'Producing about 2,200 lbs of copper requires 2,500 to 3,200 cubic feet of groundwater, equivalent to filling a small swimming pool. Some of the largest deposits are located in Chile and Peru, including areas of the Atacama Desert, one of the most water-scarce environments on Earth. Local communities increasingly oppose new mines, fearing the depletion of already scarce water resources. Tailings dams can worsen environmental damage if they leak, allowing heavy metals and acidic drainage to contaminate nearby groundwater and waterways over time.',
  },
  envSoilText: {
    id: 'rm_metal_copper_env_soil_text',
    defaultMessage:
      "Heavy metals can build up in the soil around copper mines due to dust from tailings facilities, leaks from tailings dams, and emissions from smelters. These metals spread through air and water, entering the food chain via crops and drinking water. In Zambia's copper belt alone, tailing facilities cover more than 9,125 hectares. Without proper rehabilitation, contamination persists for hundreds of years after a mine closes.",
  },
  envBiodiversityText: {
    id: 'rm_metal_copper_env_biodiversity_text',
    defaultMessage:
      'In fact, copper extraction generates highly acidic water loaded with heavy metals, seeping into rivers and soils far beyond the mine and acidifying entire ecosystems. The most severe cases are in Chile, Peru, and Indonesia. Mines also open remote areas to roads, settlements, and poaching, with deforestation documented up to 70 kilometres from mining sites. According to the IUCN Red List, the mining industry threatens as many species worldwide as climate change: approximately 11,000.',
  },
  envClimateText: {
    id: 'rm_metal_copper_env_climate_text',
    defaultMessage:
      'Copper has one of the largest climate footprints among all metals, driven by high production volumes and the extreme temperatures required to smelt sulfide ores. As ore grades decline, mines must dig deeper and process more rock for the same output, pushing emissions higher over time. Globally, mineral and metal production accounts for about 10% of total energy-related greenhouse gas emissions.',
  },
  humanSummaryText: {
    id: 'rm_metal_copper_human_summary',
    defaultMessage:
      'Over the past 13 years, copper mining has been linked to more documented human rights abuses than the mining of any other metal used in technology. In Chile and Peru, mining operations have directly affected water systems vital to local and Indigenous communities, fuelling ongoing conflicts and protests. The sections below focus on Chile, the world’s largest copper producer, accounting for approximately 23% of global output. While social challenges vary across countries, the underlying patterns of conflict documented here can be found across copper-producing regions.',
  },
  humanDisplacementText: {
    id: 'rm_metal_copper_human_displacement_text',
    defaultMessage:
      'Copper mining in Chile is concentrated in the arid north, where land and water disputes are endemic. Mining companies frequently fail to genuinely involve Indigenous communities in decision-making before operations begin. This lack of consultation and flawed FPIC process, a right recognised under international frameworks, is a primary driver of conflict. When communities are excluded from decisions affecting their land and water, protests and confrontations follow.',
  },
  humanWaterText: {
    id: 'rm_metal_copper_human_water_text',
    defaultMessage:
      'In northern Chile, mining is one of the largest users of water in a critically water-scarce region. Although seawater is projected to account for 67.6% of the copper-mining sector’s total water consumption by 2034, according to the Chilean Copper Commission[^*], many operations still rely on freshwater, competing with communities, farmers, and ecosystems for limited resources. Copper mining can also release heavy metals and other toxic substances into groundwater, threatening human health and ecosystems long after mines close.',
  },
  humanHealthText: {
    id: 'rm_metal_copper_human_health_text',
    defaultMessage:
      'Mine workers face significant health risks: silica dust causing irreversible lung disease, noise-induced cardiovascular problems, and hearing damage. High rates of fatigue, anxiety, and depression are also linked to demanding shift patterns. Research shows a direct correlation between copper prices and fatalities: as prices rise, production pressure increases and accidents multiply. In 2024, following several high-profile accidents, Chile ratified an international convention on mine worker safety.',
  },
  humanLegalText: {
    id: 'rm_metal_copper_human_legal_text',
    defaultMessage:
      'An estimated 60% of medium- and large-scale copper mines in Chile face disputes with local communities, suggesting that consultation frameworks are insufficient, especially over water. Even in Chile, despite relatively strong institutions, Indigenous rights are routinely overridden by mining interests. When legal channels fail, protests and road blockades may be among the few avenues to defend lands and water, and have already threatened the stability of Chile’s copper supply to global markets.',
  },
  geoSummaryText: {
    id: 'rm_metal_copper_geo_summary',
    defaultMessage:
      "Copper hasn't been used as a political weapon yet at scale but the pieces are already in place. Several producing countries have quietly started restricting exports and on top of that, China refines 44% of the world's copper. The supply chain is far more politically fragile than it looks.",
  },
  geoDugText: {
    id: 'rm_metal_copper_geo_dug_text',
    defaultMessage:
      "The world's copper is dominated by two countries: Chile (23%) and the DRC (14%). Peru and China follow, supplying 11% and 8% respectively.",
  },
  geoProcessedText: {
    id: 'rm_metal_copper_geo_processed_text',
    defaultMessage:
      'China dominates at 44.4%, refining nearly half of all global copper, and its share remained stable between 2023 and 2024.',
  },
  geoLeverageText: {
    id: 'rm_metal_copper_geo_leverage_text',
    defaultMessage:
      'Recent developments[^**] involving Indonesia, the United States, and Chile show how copper is becoming a source of geopolitical leverage. Indonesia has restricted exports of unprocessed copper and other metals, including exports to the United States, requiring copper to be refined domestically as part of its downstream-processing policy. Chile is the world’s largest copper producer and supplies around one-quarter of global mined output, making it increasingly central to competition over critical minerals. China remains a major destination for Chilean copper exports, while Chile and the United States have strengthened cooperation to secure critical-mineral supply chains.',
  },
  supplySummaryText: {
    id: 'rm_metal_copper_supply_summary',
    defaultMessage:
      'Copper faces one of the largest supply gaps of any metal: by 2035, the world could be short of 30 to 40% of the copper it needs. Ore quality has fallen 40% since 1991, making extraction harder and costlier every year.',
  },
  supplyReservesText: {
    id: 'rm_metal_copper_supply_reserves_text',
    defaultMessage:
      'Supply risk is near-term: the problem is not simply how much copper remains in the ground, but whether new discoveries and mine development can keep pace with future demand. New discoveries have slowed sharply, while existing deposits are becoming harder to mine: average ore grades have fallen by 40% since 1991.',
  },
  supplyDemandText: {
    id: 'rm_metal_copper_supply_demand_text',
    defaultMessage:
      'Electric vehicles use four times as much copper as conventional cars. Every wind turbine, solar panel, and power-grid upgrade adds to demand. By 2050, copper demand from clean-energy technologies is expected to grow by as much as 35%, placing significant strain on supply. Closing the gap would require $500 billion in new mining investment, with no guarantee that this funding will materialise.',
  },
  recyclingSummaryText: {
    id: 'rm_metal_copper_recycling_summary',
    defaultMessage:
      'Copper recycling exists, but it remains a partial system: it works in some sectors but not all. But above anything else, the trend is moving in the wrong direction.',
  },
  recyclingInputText: {
    id: 'rm_metal_copper_recycling_input_text',
    defaultMessage:
      "Copper's recycled share dropped from 37% to 33% between 2015 and 2023, as demand is outpacing the recycling system.",
  },
  recyclingOutlookText: {
    id: 'rm_metal_copper_recycling_outlook_text',
    defaultMessage:
      'Probably not. Without urgent action, the world will need 30% more mining investment just to fill the gap. The system is losing ground.',
  },
  footnote1Text1: {
    id: 'rm_metal_copper_footnote_1_p1',
    defaultMessage:
      'Cochilco reports that seawater is expected to increase from 40.7% of the sector’s water consumption in 2024 to 67.6% by 2034.',
  },
  footnote1Text2: {
    id: 'rm_metal_copper_footnote_1_p2',
    defaultMessage:
      'Comisión Chilena del Cobre (Cochilco), Proyección de la demanda de agua en la minería del cobre 2025–2034 (Santiago: Cochilco, 2026), [https://www.cochilco.cl/web/download/1014/2026/15581/informe-proyeccion-de-la-demanda-de-agua-en-la-mineria-del-cobre-en-chile-periodo-2025-2034.pdf](https://www.cochilco.cl/web/download/1014/2026/15581/informe-proyeccion-de-la-demanda-de-agua-en-la-mineria-del-cobre-en-chile-periodo-2025-2034.pdf).',
  },
  footnote2Text1: {
    id: 'rm_metal_copper_footnote_2_p1',
    defaultMessage:
      'Bambang Ismoyo, “Indonesia Restricts Copper Exports to US to Refined Products Only,” Jakarta Globe, July 25, 2025, [https://jakartaglobe.id/business/indonesia-restricts-copper-exports-to-us-to-refined-products-only](https://jakartaglobe.id/business/indonesia-restricts-copper-exports-to-us-to-refined-products-only).',
  },
  footnote2Text2: {
    id: 'rm_metal_copper_footnote_2_p2',
    defaultMessage:
      'Oliver Wright, “US and Chile Sign Joint Declaration on Critical Minerals and Rare Earth Elements: Top Points,” DLA Piper, March 17, 2026, [https://www.dlapiper.com/en-us/insights/publications/2026/03/us-chile-critical-minerals-declaration](https://www.dlapiper.com/en-us/insights/publications/2026/03/us-chile-critical-minerals-declaration).',
  },
  mine0Region: {
    id: 'rm_metal_copper_mine0_region',
    defaultMessage: 'Escondida',
  },
  mine1Region: {
    id: 'rm_metal_copper_mine1_region',
    defaultMessage: 'El Teniente',
  },
  mine2Region: {
    id: 'rm_metal_copper_mine2_region',
    defaultMessage: 'Cerro Verde',
  },
  mine3Region: {
    id: 'rm_metal_copper_mine3_region',
    defaultMessage: 'Las Bambas',
  },
  mine4Region: {
    id: 'rm_metal_copper_mine4_region',
    defaultMessage: 'Olympic Dam',
  },
} as const
